import Colors, { blendWithBlack, isLightColor } from '../../../../../ui/global/Colors';
import { defaultConfig } from '../../../../../utils/config';

// Resolve agent key from SSE text meta
export function resolveAgentKeyFromMeta(meta?: { task_tool_id?: string }): string | undefined {
  if (!meta) return undefined;
  return meta.task_tool_id as string | undefined;
}

// Resolve agent key from a tool view model / payload
export function resolveAgentKeyFromTool(tool: any): string | undefined {
  try {
    const name = (tool?.name || '').toString();
    // Agent-only tools should never be classified into sub-agent columns
    const agentOnly = new Set(['merge_code']);
    if (agentOnly.has(name)) return undefined;
    const p: any = tool?.params || {};
    const fromMeta = p?.meta && (p.meta.task_tool_id);
    const fromResultMeta = p?.result?.meta && (p.result.meta.task_tool_id);
    const direct = p.task_tool_id; // legacy fallback if ever present
    const derived = (fromMeta || fromResultMeta || direct);
    if (derived != null) return String(derived);
    // Avoid deriving column key from umbrella task (no task_tool_id meta present)
    if (name === 'task') return undefined;
    return undefined;
  } catch {
    return undefined;
  }
}

// Track unique color assignments to ensure distinct colors per session
const assignedAgentKeyToColor = new Map<string, string>();
const usedColors = new Set<string>();

// Cache Lab conversions for palette hex strings
type Lab = { L: number; a: number; b: number };
const hexToLabCache = new Map<string, Lab>();

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return { r, g, b };
}

function srgbToLinear(c: number): number {
  const cs = c / 255;
  return cs <= 0.04045 ? cs / 12.92 : Math.pow((cs + 0.055) / 1.055, 2.4);
}

function rgbToXyz(r: number, g: number, b: number): { x: number; y: number; z: number } {
  const rl = srgbToLinear(r);
  const gl = srgbToLinear(g);
  const bl = srgbToLinear(b);
  const x = rl * 0.4124564 + gl * 0.3575761 + bl * 0.1804375;
  const y = rl * 0.2126729 + gl * 0.7151522 + bl * 0.0721750;
  const z = rl * 0.0193339 + gl * 0.1191920 + bl * 0.9503041;
  return { x, y, z };
}

function xyzToLab(x: number, y: number, z: number): Lab {
  // D65 reference white
  const Xn = 0.95047; // 95.047/100
  const Yn = 1.0;     // 100/100
  const Zn = 1.08883; // 108.883/100

  const fx = (t: number) => {
    const e = 0.008856; // epsilon
    const k = 903.3;    // kappa
    return t > e ? Math.cbrt(t) : (k * t + 16) / 116;
  };

  const fX = fx(x / Xn);
  const fY = fx(y / Yn);
  const fZ = fx(z / Zn);

  const L = 116 * fY - 16;
  const a = 500 * (fX - fY);
  const b = 200 * (fY - fZ);
  return { L, a, b };
}

function hexToLab(hex: string): Lab {
  const cached = hexToLabCache.get(hex);
  if (cached) return cached;
  const { r, g, b } = hexToRgb(hex);
  const { x, y, z } = rgbToXyz(r, g, b);
  const lab = xyzToLab(x, y, z);
  hexToLabCache.set(hex, lab);
  return lab;
}

function deltaE(l1: Lab, l2: Lab): number {
  const dL = l1.L - l2.L;
  const da = l1.a - l2.a;
  const db = l1.b - l2.b;
  return Math.sqrt(dL * dL + da * da + db * db);
}

function circularAdjacent(i: number, j: number, n: number): boolean {
  if (n <= 1) return false;
  const d = Math.abs(i - j);
  return d === 1 || d === n - 1;
}

// Deterministically map key to color in palette
export function getAgentColorByKey(key?: string): string | undefined {
  if (!defaultConfig.STREAM_SUB_AGENT_COLORS) return undefined;
  if (!key) return undefined;
  const existing = assignedAgentKeyToColor.get(key);
  if (existing) return existing;

  const palette = [
    Colors.colorRed,
    Colors.colorOrange,
    Colors.colorAmber,
    Colors.colorYellow,
    Colors.colorLime,
    Colors.colorGreen,
    Colors.colorEmerald,
    Colors.colorTeal,
    Colors.colorCyan,
    Colors.colorSky,
    Colors.colorBlue,
    Colors.colorIndigo,
    Colors.colorViolet,
    Colors.colorPurple,
    Colors.colorFuchsia,
    Colors.colorPink,
    Colors.colorRose,
  ];

  // Stable hash for base index and tie-breaking
  let h = 5381;
  for (let i = 0; i < key.length; i++) { h = ((h << 5) + h) + key.charCodeAt(i); h |= 0; }
  const baseIdx = Math.abs(h) % palette.length;

  // Precompute Lab values for palette
  const paletteLab = palette.map(hexToLab);

  // Compute used indices
  const usedIndices: number[] = [];
  usedColors.forEach((c) => {
    const idx = palette.indexOf(c);
    if (idx >= 0) usedIndices.push(idx);
  });

  let chosenIdx: number;

  if (usedIndices.length === 0) {
    chosenIdx = baseIdx;
  } else {
    const available: number[] = [];
    for (let i = 0; i < palette.length; i++) {
      if (!usedColors.has(palette[i])) available.push(i);
    }

    // If palette exhausted (unlikely for 2–6 agents), fall back to baseIdx
    if (available.length === 0) {
      chosenIdx = baseIdx;
    } else {
      const nonAdjacent = available.filter(i => !usedIndices.some(u => circularAdjacent(i, u, palette.length)));
      const candidates = nonAdjacent.length > 0 ? nonAdjacent : available;

      let bestScore = -1;
      let bestIndices: number[] = [];
      for (const i of candidates) {
        const minDistance = usedIndices.reduce((minD, uIdx) => {
          const d = deltaE(paletteLab[i], paletteLab[uIdx]);
          return d < minD ? d : minD;
        }, Number.POSITIVE_INFINITY);
        if (minDistance > bestScore) {
          bestScore = minDistance;
          bestIndices = [i];
        } else if (minDistance === bestScore) {
          bestIndices.push(i);
        }
      }

      if (bestIndices.length === 1) {
        chosenIdx = bestIndices[0];
      } else {
        // Deterministic tie-break: prefer closest (circular) to baseIdx, then lowest index
        const circDist = (i: number) => Math.min((i - baseIdx + palette.length) % palette.length, (baseIdx - i + palette.length) % palette.length);
        bestIndices.sort((a, b) => {
          const da = circDist(a);
          const db = circDist(b);
          if (da !== db) return da - db;
          return a - b;
        });
        chosenIdx = bestIndices[0];
      }
    }
  }

  const selected = palette[chosenIdx];
  assignedAgentKeyToColor.set(key, selected);
  usedColors.add(selected);
  return selected;
}

export function getUIColors(key?: string): { bg?: string; border?: string; fg?: string } {
  const color = getAgentColorByKey(key);
  if (!color) return {};
  const bg = blendWithBlack(color, 0.8);
  const border = blendWithBlack(color, 0.5);
  const fg = isLightColor(bg) ? Colors.grey7 : Colors.grey2;
  return { bg, border, fg };
}

export function shouldColorize(name?: string): boolean {
  return name !== 'classify';
}

export function resetSubAgentColorAssignments(): void {
  assignedAgentKeyToColor.clear();
  usedColors.clear();
}
